import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Mail, Send, CheckCircle2 } from "lucide-react";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState({ state: "idle", error: "" });

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ state: "sending", error: "" });
        try {
            await api.post("/contact", form);
            setStatus({ state: "success", error: "" });
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            setStatus({
                state: "idle",
                error: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
            <header>
                <p className="eyebrow">Contact Us</p>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 leading-tight">
                    Say hello — we read every message
                </h1>
                <p className="text-[var(--lt-muted)] mt-3 max-w-2xl text-sm sm:text-base">
                    Feedback, ideas, feature requests or bug reports — send it all
                    our way. You can also reach us at{" "}
                    <a
                        href="mailto:hello@lipityping.com"
                        className="text-[var(--lt-terracotta)] font-semibold"
                    >
                        hello@lipityping.com
                    </a>
                    .
                </p>
            </header>

            <div className="glass rounded-2xl p-6 sm:p-8 mt-10">
                {status.state === "success" ? (
                    <div
                        className="flex flex-col items-center text-center py-10"
                        data-testid="contact-success"
                    >
                        <CheckCircle2 className="text-[var(--lt-emerald)]" size={48} />
                        <h2 className="text-xl font-semibold mt-4">Message received</h2>
                        <p className="text-sm text-[var(--lt-muted)] mt-2 max-w-md">
                            Thanks for reaching out! We usually reply within one to
                            two business days.
                        </p>
                        <button
                            onClick={() => setStatus({ state: "idle", error: "" })}
                            className="btn-ghost-line text-sm mt-6"
                            data-testid="contact-send-another-btn"
                        >
                            Send another message
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                        <div>
                            <label className="eyebrow block mb-2">Name</label>
                            <input
                                required
                                value={form.name}
                                onChange={set("name")}
                                className="w-full bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lt-terracotta)]"
                                placeholder="Your name"
                                data-testid="contact-name-input"
                            />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Email</label>
                            <input
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                className="w-full bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lt-terracotta)]"
                                placeholder="you@example.com"
                                data-testid="contact-email-input"
                            />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Message</label>
                            <textarea
                                required
                                rows={6}
                                value={form.message}
                                onChange={set("message")}
                                className="w-full bg-[var(--lt-bg-2)] border border-[var(--lt-border)] rounded-lg px-4 py-3 focus:outline-none focus:border-[var(--lt-terracotta)] resize-none"
                                placeholder="What is on your mind?"
                                data-testid="contact-message-input"
                            />
                        </div>
                        {status.error && (
                            <p className="text-sm text-red-400" data-testid="contact-error">
                                {status.error}
                            </p>
                        )}
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <a
                                href="mailto:hello@lipityping.com"
                                className="text-sm text-[var(--lt-muted)] inline-flex items-center gap-2"
                            >
                                <Mail size={14} /> hello@lipityping.com
                            </a>
                            <button
                                type="submit"
                                disabled={status.state === "sending"}
                                className="btn-terra inline-flex items-center gap-2 disabled:opacity-60"
                                data-testid="contact-submit-btn"
                            >
                                <Send size={14} />
                                {status.state === "sending" ? "Sending…" : "Send message"}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
